import { useEffect, useState } from 'react';
import { getPaymentRecoveryPrompt } from '../api/paymentRecoveryApi';

/** How often the charging screen asks whether a card confirmation is needed. */
export const PAYMENT_RECOVERY_POLL_MS = 15000;

/**
 * Whether this session's customer is being asked to confirm their card
 * again (payment recovery, sw-ev-api docs/PAYMENT_RECOVERY.md).
 *
 * Reads on mount and every 15 s while the session is active; stops once it
 * has ended. A failed read is silent and keeps the last answer — the prompt
 * neither appears nor disappears because of a network blip.
 *
 * @param {object} params
 * @param {string|null} params.sessionUid
 * @param {boolean} [params.ended] - the session has ended; stop reading.
 * @param {number} [params.pollMs]
 * @returns {{ prompt: object|null }} the API's answer for this session, or null.
 */
export default function usePaymentRecoveryPrompt({ sessionUid, ended = false, pollMs = PAYMENT_RECOVERY_POLL_MS } = {}) {
  // Keyed by session, so one session's prompt is never shown for another.
  const [held, setHeld] = useState({ uid: null, prompt: null });
  const active = !!sessionUid && !ended;

  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    let inFlight = false;

    const read = async () => {
      if (inFlight) return;
      inFlight = true;
      const result = await getPaymentRecoveryPrompt(sessionUid);
      inFlight = false;
      if (cancelled || !result.ok) return;
      setHeld({ uid: sessionUid, prompt: result.prompt });
    };

    read();
    const interval = setInterval(read, pollMs);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [active, sessionUid, pollMs]);

  return { prompt: held.uid === sessionUid ? held.prompt : null };
}
